import { useState, useEffect } from 'react';
import { doc, updateDoc, arrayRemove, collection, query, orderBy, onSnapshot, addDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../../firebase';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { IconArrowUp, IconArrowDown, IconTrash, IconPlus, IconExternalLink } from '@tabler/icons-react';
import { inputStyle } from '../contracts/contractsApi';
import {
  addItem, updateItem, deleteItem, updateProject, swapOrder, setPhaseStatus, setDeliverableStatus, addDocument, fileUrl, deleteFile, uploadProjectFile,
  toDateInput, fromDateInput, serverTimestamp,
  PHASE_STATUSES, DELIVERABLE_STATUSES, DOC_CATEGORIES,
} from './projectsApi';

// ---------- shared bits ----------

const fmt = (ts) => (ts?.toDate ? ts.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const daysSince = (ts) => (ts?.toDate ? Math.max(0, Math.floor((Date.now() - ts.toDate().getTime()) / 86400000)) : null);
const small = { ...inputStyle, height: 28, fontSize: 12 };
const muted = { color: 'var(--color-text-tertiary)', fontSize: 11 };
const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

export function Banner({ tone, children, onClose }) {
  return (
    <div style={{ padding: '8px 12px', background: `var(--color-${tone}-light)`, color: `var(--color-${tone})`, borderRadius: 'var(--radius-md)', fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <span>{children}</span>{onClose && <span style={{ cursor: 'pointer' }} onClick={onClose}>Dismiss</span>}
    </div>
  );
}

// Click to edit; saves on blur or Enter (Cmd/Ctrl+Enter for multi-line).
export function Editable({ value, onSave, multiline = false, placeholder = 'Add…', style }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || '');
  useEffect(() => { if (!editing) setV(value || ''); }, [value, editing]);
  const save = () => { setEditing(false); if ((v || '').trim() !== (value || '').trim()) onSave(v.trim()); };
  if (!editing) {
    return (
      <div onClick={() => setEditing(true)} title="Click to edit"
        style={{ cursor: 'text', minHeight: 18, whiteSpace: multiline ? 'pre-wrap' : 'normal', color: value ? 'inherit' : 'var(--color-text-tertiary)', ...style }}>
        {value || placeholder}
      </div>
    );
  }
  const common = {
    autoFocus: true, value: v, onChange: e => setV(e.target.value), onBlur: save,
    onKeyDown: e => { if (e.key === 'Escape') { setV(value || ''); setEditing(false); } if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); } },
  };
  return multiline
    ? <textarea {...common} rows={3} style={{ ...inputStyle, height: 'auto', padding: 8, fontSize: 12, resize: 'vertical' }} />
    : <input {...common} style={{ ...small }} />;
}

function Section({ title, subtitle, children, onAdd, addLabel }) {
  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12 }}>
        <div>
          <div style={{ fontSize: 14 }}>{title}</div>
          {subtitle && <div style={{ ...muted, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {onAdd && <Button size="sm" variant="secondary" icon={<IconPlus size={12} stroke={2} />} onClick={onAdd}>{addLabel}</Button>}
      </div>
      {children}
    </Card>
  );
}

function AddForm({ fields, onSubmit, onCancel, submitLabel = 'Add' }) {
  const [v, setV] = useState(Object.fromEntries(fields.map(f => [f.key, f.initial ?? ''])));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    const ok = await onSubmit(v);
    setBusy(false);
    if (ok !== false) onCancel();
  };
  return (
    <div style={{ padding: 12, background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', marginBottom: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {fields.map(f => {
        const set = e => setV({ ...v, [f.key]: f.type === 'file' ? e.target.files[0] || null : e.target.value });
        if (f.type === 'select') return <select key={f.key} style={{ ...small, width: f.width || 160 }} value={v[f.key]} onChange={set}>{f.options.map(([o, l]) => <option key={o} value={o}>{l}</option>)}</select>;
        if (f.type === 'textarea') return <textarea key={f.key} rows={2} placeholder={f.placeholder} style={{ ...inputStyle, height: 'auto', padding: 8, fontSize: 12, width: f.width || 360 }} value={v[f.key]} onChange={set} />;
        if (f.type === 'file') return <input key={f.key} type="file" style={{ fontSize: 11, width: f.width || 220 }} onChange={set} />;
        return <input key={f.key} type={f.type || 'text'} placeholder={f.placeholder} style={{ ...small, width: f.width || 220 }} value={v[f.key]} onChange={set} />;
      })}
      <Button size="sm" loading={busy} onClick={submit}>{submitLabel}</Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
    </div>
  );
}

function Move({ list, item, onSwap }) {
  const i = list.findIndex(x => x.id === item.id);
  return (
    <span style={{ display: 'inline-flex' }}>
      <Button size="sm" variant="ghost" disabled={i <= 0} onClick={() => onSwap(item, list[i - 1])} icon={<IconArrowUp size={12} stroke={1.5} />} />
      <Button size="sm" variant="ghost" disabled={i >= list.length - 1} onClick={() => onSwap(item, list[i + 1])} icon={<IconArrowDown size={12} stroke={1.5} />} />
    </span>
  );
}

const Del = ({ onClick }) => <Button size="sm" variant="ghost" onClick={onClick} icon={<IconTrash size={12} stroke={1.5} />} />;

const guard = (onError) => async (fn) => { try { await fn(); return true; } catch (e) { onError(e.message); return false; } };

// ---------- phases ----------

export function PhasesSection({ projectId, project, data, onError }) {
  const [adding, setAdding] = useState(false);
  const run = guard(onError);
  const phases = [...(data.phases || [])].sort(byOrder);
  const deliverableCount = (id) => (data.deliverables || []).filter(d => d.phaseId === id).length;
  return (
    <Section title="Phases" subtitle="Clients see the in-progress phase as Happening now, delivered ones under Delivered, the rest as Coming next (no dates)."
      onAdd={() => setAdding(true)} addLabel="Add phase">
      {adding && <AddForm fields={[{ key: 'name', placeholder: 'Phase name', width: 260 }, { key: 'summary', type: 'textarea', placeholder: 'What this phase gives the client, in plain English' }]}
        onCancel={() => setAdding(false)}
        onSubmit={v => (v.name.trim() ? run(() => addItem(projectId, 'phases', {
          name: v.name.trim(), summary: v.summary.trim(), order: (phases.at(-1)?.order ?? 0) + 1, status: 'not_started', targetDate: null, startedAt: null, deliveredAt: null,
        })) : (onError('Give the phase a name.'), false))} />}
      {phases.length === 0 && <div style={muted}>No phases yet. The portal can't be opened until there is at least one.</div>}
      {phases.map(p => (
        <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '56px 1fr 150px 150px 28px', gap: 10, padding: '10px 0', borderTop: '0.5px solid var(--color-border-light)', alignItems: 'start', fontSize: 12 }}>
          <Move list={phases} item={p} onSwap={(a, b) => run(() => swapOrder(projectId, 'phases', a, b))} />
          <div>
            <Editable value={p.name} style={{ fontWeight: 500 }} onSave={v => run(() => updateItem(projectId, 'phases', p.id, { name: v }))} />
            <Editable value={p.summary} multiline placeholder="Add a summary…" style={{ color: 'var(--color-text-secondary)', marginTop: 4 }}
              onSave={v => run(() => updateItem(projectId, 'phases', p.id, { summary: v }))} />
            <div style={{ ...muted, marginTop: 4 }}>
              {p.status === 'in_progress' && <span style={{ color: 'var(--color-brand)' }}>Current · </span>}
              {deliverableCount(p.id)} deliverables{p.startedAt ? ` · started ${fmt(p.startedAt)}` : ''}{p.deliveredAt ? ` · delivered ${fmt(p.deliveredAt)}` : ''}
            </div>
          </div>
          <select style={small} value={p.status} onChange={e => run(() => setPhaseStatus(projectId, project, p, e.target.value))}>
            {PHASE_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <div>
            <input type="date" style={small} value={toDateInput(p.targetDate)} onChange={e => run(() => updateItem(projectId, 'phases', p.id, { targetDate: fromDateInput(e.target.value) }))} />
            <div style={{ ...muted, marginTop: 3 }}>Target, shown only while in progress</div>
          </div>
          <Del onClick={() => window.confirm(`Delete phase "${p.name}"?${deliverableCount(p.id) ? ` Its ${deliverableCount(p.id)} deliverables will be left without a phase.` : ''}`)
            && run(async () => { await deleteItem(projectId, 'phases', p.id); if (project.currentPhaseId === p.id) await updateProject(projectId, { currentPhaseId: null }); })} />
        </div>
      ))}
    </Section>
  );
}

// ---------- deliverables ----------

export function DeliverablesSection({ projectId, data, onError }) {
  const [adding, setAdding] = useState(false);
  const run = guard(onError);
  const phases = [...(data.phases || [])].sort(byOrder);
  const all = data.deliverables || [];
  const groups = [...phases.map(p => ({ id: p.id, name: p.name })), { id: '__none', name: 'No phase' }]
    .map(g => ({ ...g, items: all.filter(d => (g.id === '__none' ? !phases.some(p => p.id === d.phaseId) : d.phaseId === g.id)).sort(byOrder) }))
    .filter(g => g.items.length || g.id !== '__none');
  return (
    <Section title="Deliverables" subtitle="What the client can actually do with each piece. Add a link when there is something to go and use."
      onAdd={() => (phases.length ? setAdding(true) : onError('Add a phase first.'))} addLabel="Add deliverable">
      {adding && <AddForm fields={[
        { key: 'phaseId', type: 'select', options: phases.map(p => [p.id, p.name]), initial: phases.find(p => p.status === 'in_progress')?.id || phases[0]?.id, width: 200 },
        { key: 'title', placeholder: 'Title', width: 280 },
        { key: 'description', type: 'textarea', placeholder: 'What the client can do with it' },
        { key: 'linkUrl', placeholder: 'Link (optional)', type: 'url', width: 260 },
      ]} onCancel={() => setAdding(false)}
        onSubmit={v => (v.title.trim() ? run(() => addItem(projectId, 'deliverables', {
          phaseId: v.phaseId, title: v.title.trim(), description: v.description.trim(), linkUrl: v.linkUrl.trim() || null,
          status: 'planned', deliveredAt: null, order: (all.filter(d => d.phaseId === v.phaseId).sort(byOrder).at(-1)?.order ?? 0) + 1,
        })) : (onError('Give the deliverable a title.'), false))} />}
      {groups.map(g => (
        <div key={g.id} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '8px 0 4px' }}>{g.name}</div>
          {g.items.length === 0 && <div style={{ ...muted, paddingBottom: 6 }}>Nothing in this phase yet.</div>}
          {g.items.map(d => (
            <div key={d.id} style={{ display: 'grid', gridTemplateColumns: '56px 1fr 220px 130px 28px', gap: 10, padding: '8px 0', borderTop: '0.5px solid var(--color-border-light)', alignItems: 'start', fontSize: 12 }}>
              <Move list={g.items} item={d} onSwap={(a, b) => run(() => swapOrder(projectId, 'deliverables', a, b))} />
              <div>
                <Editable value={d.title} style={{ fontWeight: 500 }} onSave={v => run(() => updateItem(projectId, 'deliverables', d.id, { title: v }))} />
                <Editable value={d.description} multiline placeholder="Add a description…" style={{ color: 'var(--color-text-secondary)', marginTop: 4 }}
                  onSave={v => run(() => updateItem(projectId, 'deliverables', d.id, { description: v }))} />
                {d.deliveredAt && <div style={{ ...muted, marginTop: 4 }}>Delivered {fmt(d.deliveredAt)}</div>}
              </div>
              <Editable value={d.linkUrl} placeholder="Add link…" style={{ wordBreak: 'break-all', color: d.linkUrl ? 'var(--color-brand)' : undefined }}
                onSave={v => run(() => updateItem(projectId, 'deliverables', d.id, { linkUrl: v || null }))} />
              <select style={small} value={d.status} onChange={e => run(() => setDeliverableStatus(projectId, d, e.target.value))}>
                {DELIVERABLE_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <Del onClick={() => window.confirm(`Delete "${d.title}"?`) && run(() => deleteItem(projectId, 'deliverables', d.id))} />
            </div>
          ))}
        </div>
      ))}
    </Section>
  );
}

// ---------- blockers ----------

// Clients mark their own items done in the portal ('client_done'); they stay
// open in the portal until Agriware approves (closes) or reopens them.
export function BlockersSection({ projectId, data, users, onError }) {
  const [adding, setAdding] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [thread, setThread] = useState(null);
  const run = guard(onError);
  const who = (uid) => users.find(u => u.id === uid)?.name || users.find(u => u.id === uid)?.email || 'a client user';
  const all = [...(data.blockers || [])].sort((a, b) => (b.raisedAt?.toMillis?.() || 0) - (a.raisedAt?.toMillis?.() || 0));
  const awaiting = all.filter(b => b.status === 'client_done');
  const open = all.filter(b => b.status === 'open');
  const resolved = all.filter(b => b.status === 'resolved');
  const approve = (b) => run(() => updateItem(projectId, 'blockers', b.id, { status: 'resolved', resolvedAt: serverTimestamp(), approvedByUid: auth.currentUser?.uid || null, reopenNote: null }));
  const reopen = (b) => {
    const note = window.prompt(`Reopen "${b.title}". What is still needed? The client sees this note.`);
    if (note === null) return;
    if (!note.trim()) { onError('Say what is still needed, so the client knows why it was reopened.'); return; }
    run(async () => {
      await updateItem(projectId, 'blockers', b.id, { status: 'open', resolvedAt: null, clientDoneAt: null, clientDoneByUid: null, reopenNote: note.trim(), reopenedAt: serverTimestamp() });
      await addAgriwareNote(projectId, b.id, `Reopened: ${note.trim()}`, []);
    });
  };
  const row = (b) => (
    <div key={b.id} style={{ borderTop: '0.5px solid var(--color-border-light)', padding: '8px 0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 150px 150px 28px', gap: 10, alignItems: 'start', fontSize: 12, opacity: b.status === 'resolved' ? 0.6 : 1 }}>
        <div>
          <Editable value={b.title} style={{ fontWeight: 500 }} onSave={v => run(() => updateItem(projectId, 'blockers', b.id, { title: v }))} />
          <Editable value={b.description} multiline placeholder="What is needed and why it matters…" style={{ color: 'var(--color-text-secondary)', marginTop: 4 }}
            onSave={v => run(() => updateItem(projectId, 'blockers', b.id, { description: v }))} />
          <span style={{ ...muted, cursor: 'pointer', color: 'var(--color-brand)', display: 'inline-block', marginTop: 4 }} onClick={() => setThread(thread === b.id ? null : b.id)}>
            {thread === b.id ? 'Hide notes' : 'Notes and files'}
          </span>
        </div>
        <select style={small} value={b.owner} onChange={e => run(() => updateItem(projectId, 'blockers', b.id, { owner: e.target.value }))}>
          <option value="client">Client</option><option value="agriware">Agriware</option>
        </select>
        <div style={muted}>
          {b.status === 'open' && `Open ${daysSince(b.raisedAt) ?? 0} days`}
          {b.status === 'client_done' && <span style={{ color: 'var(--color-warning)' }}>Marked done by {who(b.clientDoneByUid)} {fmt(b.clientDoneAt)}</span>}
          {b.status === 'resolved' && `Resolved ${fmt(b.resolvedAt)}`}
          <div>Raised {fmt(b.raisedAt)}</div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {b.status === 'client_done' && <>
            <Button size="sm" variant="primary" onClick={() => approve(b)}>Approve</Button>
            <Button size="sm" variant="secondary" onClick={() => reopen(b)}>Reopen</Button>
          </>}
          {b.status === 'open' && <Button size="sm" variant="secondary" onClick={() => run(() => updateItem(projectId, 'blockers', b.id, { status: 'resolved', resolvedAt: serverTimestamp() }))}>Resolve</Button>}
          {b.status === 'resolved' && <Button size="sm" variant="ghost" onClick={() => run(() => updateItem(projectId, 'blockers', b.id, { status: 'open', resolvedAt: null, clientDoneAt: null, clientDoneByUid: null }))}>Reopen</Button>}
        </div>
        <Del onClick={() => window.confirm(`Delete "${b.title}"?`) && run(() => deleteItem(projectId, 'blockers', b.id))} />
      </div>
      {thread === b.id && <BlockerThread projectId={projectId} blocker={b} onError={onError} />}
    </div>
  );
  return (
    <Section title="Blockers" subtitle="Client-owned blockers appear in the portal under Needs you. Clients can reply, attach files and mark them done; you approve or reopen."
      onAdd={() => setAdding(true)} addLabel="Raise blocker">
      {adding && <AddForm fields={[
        { key: 'title', placeholder: 'What is needed', width: 280 },
        { key: 'description', type: 'textarea', placeholder: 'Why it matters' },
        { key: 'owner', type: 'select', options: [['client', 'Client'], ['agriware', 'Agriware']], initial: 'client', width: 120 },
      ]} onCancel={() => setAdding(false)}
        onSubmit={v => (v.title.trim() ? run(() => addItem(projectId, 'blockers', {
          title: v.title.trim(), description: v.description.trim(), owner: v.owner, status: 'open', raisedAt: serverTimestamp(), resolvedAt: null,
        })) : (onError('Say what is needed.'), false))} />}
      {awaiting.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-warning)', marginBottom: 2 }}>Awaiting your approval ({awaiting.length})</div>
          {awaiting.map(row)}
        </div>
      )}
      {open.length === 0 && awaiting.length === 0 && <div style={muted}>No open blockers.</div>}
      {open.map(row)}
      {resolved.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <span style={{ ...muted, cursor: 'pointer', color: 'var(--color-brand)' }} onClick={() => setShowResolved(s => !s)}>
            {showResolved ? 'Hide' : 'Show'} {resolved.length} resolved
          </span>
          {showResolved && resolved.map(row)}
        </div>
      )}
    </Section>
  );
}

async function addAgriwareNote(projectId, blockerId, text, attachments) {
  const u = auth.currentUser;
  await addDoc(collection(db, 'clientProjects', projectId, 'blockers', blockerId, 'notes'), {
    authorUid: u.uid, authorRole: 'agriware', authorName: u.displayName || (u.email || '').split('@')[0] || 'Agriware',
    text, attachments, createdAt: serverTimestamp(),
  });
}

// The reply thread on one blocker, as the client sees it in the portal.
function BlockerThread({ projectId, blocker, onError }) {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => onSnapshot(query(collection(db, 'clientProjects', projectId, 'blockers', blocker.id, 'notes'), orderBy('createdAt')),
    snap => setNotes(snap.docs.map(d => ({ id: d.id, ...d.data() }))), e => onError(e.message)), [projectId, blocker.id, onError]);
  const open = async (a) => {
    const w = window.open('', '_blank');
    try { w.location.href = await fileUrl(a.path); } catch (e) { w?.close(); onError(e.message); }
  };
  const send = async () => {
    if (!text.trim() && !file) return;
    setBusy(true);
    try {
      const attachments = file ? [{ name: file.name, path: await uploadProjectFile(projectId, file, `blockers/${blocker.id}/agriware`), size: file.size, contentType: file.type }] : [];
      await addAgriwareNote(projectId, blocker.id, text.trim(), attachments);
      setText(''); setFile(null);
    } catch (e) { onError(e.message); }
    setBusy(false);
  };
  return (
    <div style={{ margin: '8px 0 4px', padding: 10, background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', fontSize: 12 }}>
      {notes === null ? <div style={muted}>Loading…</div> : notes.length === 0 ? <div style={muted}>No notes yet.</div> : notes.map(n => (
        <div key={n.id} style={{ padding: '6px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
          <div style={muted}><strong style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{n.authorName || '—'}</strong>{n.authorRole === 'agriware' ? ' (Agriware)' : ' (client)'} · {n.createdAt?.toDate?.().toLocaleString('en-GB') || 'just now'}</div>
          {n.text && <div style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{n.text}</div>}
          {(n.attachments || []).map(a => <div key={a.path}><span style={{ color: 'var(--color-brand)', cursor: 'pointer' }} onClick={() => open(a)}>{a.name}</span></div>)}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={2} placeholder="Reply to the client…" maxLength={5000}
          style={{ ...inputStyle, flex: 1, minWidth: 220, height: 'auto', padding: 8, fontSize: 12 }} />
        <input type="file" style={{ fontSize: 11 }} onChange={e => setFile(e.target.files[0] || null)} />
        <Button size="sm" variant="secondary" disabled={busy || (!text.trim() && !file)} onClick={send}>{busy ? 'Sending…' : 'Send'}</Button>
      </div>
    </div>
  );
}

// ---------- decisions ----------

export function DecisionsSection({ projectId, data, users, onError }) {
  const [adding, setAdding] = useState(false);
  const run = guard(onError);
  const decisions = [...(data.decisions || [])].sort((a, b) => (b.decidedAt?.toMillis?.() || 0) - (a.decidedAt?.toMillis?.() || 0));
  const who = (uid) => users.find(u => u.id === uid)?.name || 'a client user';
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Section title="Decisions" subtitle="Clients confirm each decision in the portal. Once confirmed, only Agriware can change it, and the confirmation stays on record."
      onAdd={() => setAdding(true)} addLabel="Record decision">
      {adding && <AddForm fields={[
        { key: 'title', placeholder: 'Decision, e.g. Mail platform: Microsoft 365', width: 300 },
        { key: 'detail', type: 'textarea', placeholder: 'The decision and its consequence, in plain English' },
        { key: 'decidedAt', type: 'date', initial: today, width: 150 },
        { key: 'decidedBy', placeholder: 'Decided by, e.g. Conor Daly / Dan Derry', width: 260 },
      ]} onCancel={() => setAdding(false)}
        onSubmit={v => (v.title.trim() ? run(() => addItem(projectId, 'decisions', {
          title: v.title.trim(), detail: v.detail.trim(), decidedAt: fromDateInput(v.decidedAt) || serverTimestamp(), decidedBy: v.decidedBy.trim(),
          acknowledgedAt: null, acknowledgedByUid: null, supersededById: null,
        })) : (onError('Give the decision a title.'), false))} />}
      {decisions.length === 0 && <div style={muted}>No decisions recorded.</div>}
      {decisions.map(d => (
        <div key={d.id} style={{ display: 'grid', gridTemplateColumns: '1fr 200px 190px 28px', gap: 10, padding: '10px 0', borderTop: '0.5px solid var(--color-border-light)', alignItems: 'start', fontSize: 12 }}>
          <div style={{ opacity: d.supersededById ? 0.6 : 1 }}>
            <Editable value={d.title} style={{ fontWeight: 500, textDecoration: d.supersededById ? 'line-through' : 'none' }}
              onSave={v => run(() => updateItem(projectId, 'decisions', d.id, { title: v }))} />
            <Editable value={d.detail} multiline placeholder="Add the detail…" style={{ color: 'var(--color-text-secondary)', marginTop: 4 }}
              onSave={v => run(() => updateItem(projectId, 'decisions', d.id, { detail: v }))} />
            <div style={{ ...muted, marginTop: 4 }}>Decided {fmt(d.decidedAt)}{d.decidedBy ? ` by ${d.decidedBy}` : ''}</div>
            {d.acknowledgedAt && <div style={{ ...muted, marginTop: 2 }}>Editing a confirmed decision changes what the client agreed to. Prefer recording a new one and superseding this.</div>}
          </div>
          <div style={{ fontSize: 11, color: d.acknowledgedAt ? 'var(--color-success)' : 'var(--color-warning)' }}>
            {d.acknowledgedAt ? `Confirmed by ${who(d.acknowledgedByUid)} on ${fmt(d.acknowledgedAt)}` : 'Awaiting client confirmation'}
          </div>
          <div>
            <select style={small} value={d.supersededById || ''} onChange={e => run(() => updateItem(projectId, 'decisions', d.id, { supersededById: e.target.value || null }))}>
              <option value="">Not superseded</option>
              {decisions.filter(x => x.id !== d.id).map(x => <option key={x.id} value={x.id}>Superseded by: {x.title}</option>)}
            </select>
          </div>
          <Del onClick={() => (d.acknowledgedAt
            ? onError('A confirmed decision cannot be deleted. Record a new decision and mark this one as superseded.')
            : window.confirm(`Delete "${d.title}"?`) && run(() => deleteItem(projectId, 'decisions', d.id)))} />
        </div>
      ))}
    </Section>
  );
}

// ---------- documents ----------

export function DocumentsSection({ projectId, data, onError }) {
  const [adding, setAdding] = useState(false);
  const run = guard(onError);
  const docs = data.documents || [];
  const open = async (d) => {
    if (d.externalUrl) { window.open(d.externalUrl, '_blank', 'noreferrer'); return; }
    const w = window.open('', '_blank');
    try { w.location.href = await fileUrl(d.storagePath); } catch (e) { w?.close(); onError(e.message); }
  };
  return (
    <Section title="Documents" subtitle="Upload a file, or link to something hosted elsewhere (one or the other)." onAdd={() => setAdding(true)} addLabel="Add document">
      {adding && <AddForm submitLabel="Add" fields={[
        { key: 'title', placeholder: 'Title', width: 260 },
        { key: 'category', type: 'select', options: DOC_CATEGORIES, initial: 'discovery', width: 130 },
        { key: 'file', type: 'file' },
        { key: 'externalUrl', type: 'url', placeholder: 'or a link', width: 240 },
      ]} onCancel={() => setAdding(false)}
        onSubmit={v => (v.title.trim() ? run(() => addDocument(projectId, { title: v.title.trim(), category: v.category, file: v.file, externalUrl: v.externalUrl.trim() })) : (onError('Give the document a title.'), false))} />}
      {docs.length === 0 && <div style={muted}>No documents.</div>}
      {DOC_CATEGORIES.map(([cat, label]) => {
        const items = docs.filter(d => d.category === cat).sort((a, b) => a.title.localeCompare(b.title));
        if (!items.length) return null;
        return (
          <div key={cat}>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '8px 0 4px' }}>{label}</div>
            {items.map(d => (
              <div key={d.id} style={{ display: 'grid', gridTemplateColumns: '1fr 150px 110px 28px', gap: 10, padding: '7px 0', borderTop: '0.5px solid var(--color-border-light)', alignItems: 'center', fontSize: 12 }}>
                <Editable value={d.title} onSave={v => run(() => updateItem(projectId, 'documents', d.id, { title: v }))} />
                <span style={{ ...muted, color: 'var(--color-brand)', cursor: 'pointer', display: 'inline-flex', gap: 4, alignItems: 'center' }} onClick={() => open(d)}>
                  {d.externalUrl ? 'Open link' : 'Open file'} <IconExternalLink size={11} stroke={1.5} />
                </span>
                <span style={muted}>Added {fmt(d.addedAt)}</span>
                <Del onClick={() => window.confirm(`Delete "${d.title}"?`) && run(async () => { await deleteItem(projectId, 'documents', d.id); if (d.storagePath) await deleteFile(d.storagePath); })} />
              </div>
            ))}
          </div>
        );
      })}
    </Section>
  );
}

// ---------- client users ----------

export function ClientUsersSection({ projectId, project, users, onError }) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);
  const invite = async (name, email) => {
    setBusy(email); setNotice(null);
    try {
      await httpsCallable(functions, 'inviteClientUser')({ projectId, email, name, clientName: project.clientName });
      setNotice(`Invitation sent to ${email}.`);
      setBusy(null);
      return true;
    } catch (e) { onError(e.message); setBusy(null); return false; }
  };
  return (
    <Section title="Client users" subtitle="Each invitation emails a set-your-password link. They can only see this project, and only while the portal is open."
      onAdd={() => setAdding(true)} addLabel="Invite user">
      {notice && <div style={{ marginBottom: 8 }}><Banner tone="success" onClose={() => setNotice(null)}>{notice}</Banner></div>}
      {adding && <AddForm submitLabel="Send invitation" fields={[
        { key: 'name', placeholder: 'Full name', width: 220 },
        { key: 'email', type: 'email', placeholder: 'Email', width: 260 },
      ]} onCancel={() => setAdding(false)}
        onSubmit={v => (v.name.trim() && v.email.trim() ? invite(v.name.trim(), v.email.trim()) : (onError('Enter a name and an email.'), false))} />}
      {(project.pendingClientUsers || []).map(p => (
        <div key={p.email} style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 260px 190px', gap: 10, padding: '8px 0', borderTop: '0.5px solid var(--color-border-light)', alignItems: 'center', fontSize: 12 }}>
          <div style={{ fontWeight: 500 }}>{p.name || '—'}</div>
          <div style={{ color: 'var(--color-text-secondary)' }}>{p.email}</div>
          <div style={muted}>From the signed contract{p.role ? ` · ${p.role}` : ''} · not invited yet</div>
          <div style={{ textAlign: 'right' }}>
            <Button size="sm" loading={busy === p.email} onClick={() => invite(p.name || p.email, p.email)}>Invite</Button>
          </div>
        </div>
      ))}
      {users.length === 0 && !(project.pendingClientUsers || []).length && <div style={muted}>No client users yet.</div>}
      {users.map(u => (
        <div key={u.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 130px 130px 190px', gap: 10, padding: '8px 0', borderTop: '0.5px solid var(--color-border-light)', alignItems: 'center', fontSize: 12 }}>
          <div style={{ fontWeight: 500 }}>{u.name}</div>
          <div style={{ color: 'var(--color-text-secondary)' }}>{u.email}</div>
          <div style={muted}>Invited {fmt(u.invitedAt)}</div>
          <div style={muted}>{u.lastSeenAt ? `Last seen ${fmt(u.lastSeenAt)}` : ''}</div>
          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
            <Button size="sm" variant="ghost" loading={busy === u.email} onClick={() => invite(u.name, u.email)}>Resend invite</Button>
            <Button size="sm" variant="ghost" onClick={() => window.confirm(`Remove ${u.name}'s access to this project?`)
              && updateDoc(doc(db, 'clientUsers', u.id), { projectIds: arrayRemove(projectId) }).catch(e => onError(e.message))}>Remove</Button>
          </div>
        </div>
      ))}
    </Section>
  );
}
